import { getSyncConfigMap } from '@commenda/api/rootFi/getSyncConfigMap';
import {
  Icons,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@commenda/kakashi';
import {
  RootfiSyncConfig,
  TRootfiCompletionTrigger,
  TRootfiSyncStepDraft,
  TWorkflowStepDraft,
} from '@commenda/types';
import { RootfiCompanyUseCase } from '@prisma/client';
import { useEffect } from 'react';
import { useWorkflow } from '../../context/WorkflowContext';
import { TSelectedEntity } from '../../types/node';

// Persisted defaults; the runtime task card overrides per-state. Kept on
// the step so non-overriding card variants and previews still render copy.
const ROOTFI_SYNC_DEFAULT_NAME = 'Connect an integration to continue';
const ROOTFI_SYNC_DEFAULT_DESCRIPTION =
  'Choose an integration to connect. It takes a few seconds, and you can disconnect anytime.';

const USE_CASE_LABELS: Record<RootfiCompanyUseCase, string> = {
  SALES_TAX: 'Sales tax',
  OPERATIONAL_TRANSFER_PRICING: 'Operational transfer pricing',
  BOOKKEEPING: 'Bookkeeping',
};

// SCREAMING_SNAKE_CASE → Title Case (INCOME_STATEMENT → "Income Statement").
const formatDataModel = (dataModel: string): string =>
  dataModel
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const formatPermissions = (perms: RootfiSyncConfig['permissions']): string => {
  const parts: string[] = [];
  if (perms.CanRead) parts.push('Read');
  if (perms.CanCreate) parts.push('Create');
  if (perms.CanUpdate) parts.push('Update');
  if (perms.CanDelete) parts.push('Delete');
  return parts.length === 1 && parts[0] === 'Read'
    ? 'Read-only'
    : parts.join(' / ');
};

export const RootfiSyncNodePropertyPanel = ({
  selectedEntity,
}: {
  selectedEntity: TSelectedEntity;
}) => {
  const { workflow, setWorkflow } = useWorkflow();

  // Draft type allows partial state mid-edit; save-time validation narrows it.
  const selectedStep = workflow?.steps.find(
    (step) => step.id === selectedEntity.id
  ) as TRootfiSyncStepDraft | undefined;

  const rootfi = selectedStep?.rootfi;

  // Overwrite legacy author-typed copy on legacy steps with the canonical
  // defaults whenever this panel mounts.
  useEffect(() => {
    if (!workflow || !selectedStep) return;
    if (
      selectedStep.name === ROOTFI_SYNC_DEFAULT_NAME &&
      selectedStep.description === ROOTFI_SYNC_DEFAULT_DESCRIPTION
    )
      return;
    setWorkflow({
      ...workflow,
      steps: workflow.steps.map((step) =>
        step.id === selectedEntity.id
          ? {
              ...step,
              name: ROOTFI_SYNC_DEFAULT_NAME,
              description: ROOTFI_SYNC_DEFAULT_DESCRIPTION,
            }
          : step
      ),
    });
  }, [
    selectedEntity.id,
    selectedStep?.name,
    selectedStep?.description,
    workflow,
    setWorkflow,
    selectedStep,
  ]);

  const updateRootfi = (next: Partial<TRootfiSyncStepDraft['rootfi']>) => {
    if (!workflow) return;
    setWorkflow({
      ...workflow,
      steps: workflow.steps.map((step): TWorkflowStepDraft => {
        if (step.id !== selectedEntity.id) return step as TWorkflowStepDraft;
        const merged = {
          useCase: rootfi?.useCase,
          completeOn: rootfi?.completeOn,
          syncConfigs: rootfi?.syncConfigs,
          ...next,
        };
        if (merged.useCase) {
          merged.syncConfigs = getSyncConfigMap(
            merged.useCase as unknown as Parameters<typeof getSyncConfigMap>[0]
          );
        }
        const updatedDraft: TRootfiSyncStepDraft = {
          ...(step as TRootfiSyncStepDraft),
          rootfi: merged,
        };
        return updatedDraft;
      }) as never,
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <span className="txt-small text-fg-secondary">
        This step requires an active integration for the chosen use case before
        the workflow can continue. The end user picks the integration
        (QuickBooks, Stripe, Xero, …) at consent time.
      </span>

      <div className="flex flex-col gap-2">
        <Label>Use case</Label>
        <Select
          value={rootfi?.useCase ?? ''}
          onValueChange={(v) =>
            updateRootfi({ useCase: v as RootfiCompanyUseCase })
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="What's this connection for?" />
          </SelectTrigger>
          <SelectContent>
            {(
              [
                'SALES_TAX',
                'OPERATIONAL_TRANSFER_PRICING',
                'BOOKKEEPING',
              ] as RootfiCompanyUseCase[]
            ).map((uc) => (
              <SelectItem key={uc} value={uc}>
                {USE_CASE_LABELS[uc]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-2">
        <Label>Mark step complete when</Label>
        <Select
          value={rootfi?.completeOn ?? 'connection_established'}
          onValueChange={(v) =>
            updateRootfi({ completeOn: v as TRootfiCompletionTrigger })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="connection_established">
              Connection is set up
            </SelectItem>
            <SelectItem value="sync_complete">First sync finishes</SelectItem>
          </SelectContent>
        </Select>
        <span className="txt-small text-fg-secondary">
          {rootfi?.completeOn === 'sync_complete'
            ? 'Step waits for the initial Rootfi sync to finish before advancing. Use when downstream steps need data to be present.'
            : 'Step advances as soon as the user has connected and granted the required scopes. Sync continues in the background.'}
        </span>
      </div>

      {rootfi?.useCase &&
        rootfi?.syncConfigs &&
        rootfi.syncConfigs.length > 0 && (
          <div className="flex flex-col gap-2 pt-2 border-t border-stroke-3">
            <div className="flex items-center justify-between">
              <Label>Data we'll request access to</Label>
              <span className="txt-small text-fg-tertiary">
                {rootfi.syncConfigs.length}{' '}
                {rootfi.syncConfigs.length === 1 ? 'item' : 'items'}
              </span>
            </div>
            <span className="txt-small text-fg-secondary">
              Snapshot taken when you picked the use case. Re-pick the use case
              above to refresh against the current catalog.
            </span>
            <ul className="flex flex-col gap-1.5 mt-1">
              {rootfi.syncConfigs.map((config) => (
                <li
                  key={`${config.integrationCategory}-${config.dataModel}`}
                  className="flex items-center justify-between gap-2 px-3 py-2 rounded-md bg-bg-secondary"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Icons.Database01 className="size-4 text-text-tertiary shrink-0" />
                    <span className="txt-small truncate">
                      {formatDataModel(config.dataModel)}
                    </span>
                  </div>
                  <span className="txt-xs text-fg-tertiary shrink-0">
                    {formatPermissions(config.permissions)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
    </div>
  );
};
